import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Link as LinkIcon } from 'lucide-react';
import Collapsible from '../components/Collapsible';

interface SocialLink {
  platform: string;
  url: string;
  visible: boolean;
}

const FooterSection = () => {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [socialText, setSocialText] = useState('');
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    fetchSocialLinks();
    fetchSocialText();
  }, []);

  const fetchSocialText = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'footer_social_text')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setSocialText(data?.value || '');
    } catch (error) {
      console.error('Error fetching social text:', error);
    }
  };

  const updateSocialText = async () => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key: 'footer_social_text',
          value: socialText
        }, {
          onConflict: 'key'
        });

      if (error) throw error;
      setFeedback('Tekst succesvol bijgewerkt');
      setFeedbackType('success');
      setTimeout(() => setFeedback(''), 3000);
    } catch (error) {
      console.error('Error updating social text:', error);
      setFeedback('Er is een fout opgetreden bij het bijwerken van de tekst');
      setFeedbackType('error');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('social_links')
        .select('*')
        .order('platform', { ascending: true });

      if (error) throw error;
      setSocialLinks(data || []);
    } catch (error) {
      console.error('Error fetching social links:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSocialLink = async (platform: string, updates: Partial<SocialLink>) => {
    try {
      // If trying to make visible but no URL, prevent the update
      if (updates.visible && !updates.url) {
        const currentLink = socialLinks.find(link => link.platform === platform);
        if (!currentLink?.url) {
          alert('Please add a URL before making the link visible');
          return;
        }
      }

      const { error } = await supabase
        .from('social_links')
        .update(updates)
        .eq('platform', platform);

      if (error) throw error;
      fetchSocialLinks();
    } catch (error) {
      console.error('Error updating social link:', error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {feedback && (
        <div className={`mb-4 p-4 rounded-md ${
          feedbackType === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {feedback}
        </div>
      )}
      <div className="space-y-6">
        <Collapsible title="Social media tekst aanpassen">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Social media tekst
              </label>
              <input
                type="text"
                value={socialText}
                onChange={(e) => setSocialText(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                placeholder="Voer de social media tekst in"
              />
            </div>
            <button
              onClick={updateSocialText}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
            >
              Tekst bijwerken
            </button>
          </div>
        </Collapsible>

        <Collapsible title="Social media links aanpassen">
          {socialLinks.map((link) => (
            <div key={link.platform} className="space-y-4 my-5">
              <h2 className="text-lg font-medium text-[#5A130E] capitalize">
                {link.platform}
              </h2>
              <div className="flex gap-4 items-center">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link URL
                  </label>
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateSocialLink(link.platform, { url: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                    placeholder={`Enter ${link.platform} URL`}
                  />
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={link.visible}
                      onChange={(e) => updateSocialLink(link.platform, { visible: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#E37C64]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#E37C64]"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      {link.visible ? 'Zichtbaar' : 'Verborgen'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </Collapsible>
      </div>
    </div>
  );
};

export default FooterSection;